const DEFAULT_TIMEOUT_MS = 10000;

export enum CrossOriginAction {
  CheckLocalStorage = 'CHECK_LOCAL_STORAGE',
  GetItem = 'GET_ITEM',
  SetItem = 'SET_ITEM'
}

export interface CrossOriginMessage {
  action: CrossOriginAction;
  payload?: Record<string, unknown>;
}

/**
 * Promisifies sending messages so that we can use async/await syntax. Also adds timeout functionality for error handling
 * Creates a new message channel for each request so we can identify that the response coming back belongs to this request
 * port1 is used to send messages, and port2 is transferred over to the targetWindow to use to respond (in replyCrossOrigin)
 * @param targetWindow: the window we want to broadcast the mes to (usually `parent` or `iframe.contentWindow`)
 * @param message
 * @param timeoutMs: default 10000
 * @param targetOrigin: default '*'
 * @returns promise on response
 */
export function requestCrossOrigin<TResponse>(
  targetWindow: Window,
  message: CrossOriginMessage,
  targetOrigin: string = '*',
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<TResponse> {
  const messagePromise = new Promise<TResponse>((resolve, reject) => {
    const channel = new MessageChannel();

    channel.port1.onmessage = ({ data }) => {
      channel.port1.close();
      const response = data as TResponse | Error;
      if (response instanceof Error) {
        reject(response);
      } else {
        resolve(response);
      }
    };

    channel.port1.onmessageerror = ({ data }) => {
      channel.port1.close();
      reject(data);
    };

    targetWindow.postMessage(message, targetOrigin, [channel.port2]);
  });

  const timeoutPromise = new Promise<TResponse>((_, reject) => {
    const timeout = Math.max(timeoutMs, 0);
    setTimeout(
      () => reject(new Error(`Timeout: Unable to get response within ${timeout}ms`)),
      timeout
    );
  });

  return Promise.race([messagePromise, timeoutPromise]);
}

/**
 * Send response for requests coming from the requestCrossOrigin() function.
 * It is recommended to verify the `event.origin` before calling this function to make sure that the origin of the request is authorized.
 * For eg: `window.addEventListener('message', (event) => { if (event.origin==="https://myapp.com") replyCrossOrigin(event, myResponse) })`;
 * @param event the event object when the request is coming
 * @param response the response you want to send back
 */
export function replyCrossOrigin<TResponse>(port: MessagePort, message: TResponse | Error) {
  port.postMessage(message);
  port.close();
}
